import type { ServiceResponseDto } from '../types/api/service';
import type {
  PatinfoSearchRequestDto,
  PatinfoSearchResponseDto,
  PatinfoNewRequestDto,
  PatinfoNewResponseDto,
  PatinfoPutRequestDto,
  PatinfoPutResponseDto,
  LaunchSecureDto,
} from '../types/api/patinfo';
import { BaseServiceClient } from './BaseServiceClient';

/**
 * ProviderProfileService
 *
 * Adapter for the provider-profile backend operations:
 *   - provider_profile_search  (sync service)
 *   - provider_profile_put     (sync service)
 *   - launch_secure_put        (sync service)
 *
 * Pass the apiService (ApiService instance) injected via ProGroupsProps.
 */
export class ProviderProfileService extends BaseServiceClient {
  constructor(apiService: any) {
    super(apiService);
  }

  // ── Search ────────────────────────────────────────────────────────────────

  /**
   * Search provider profiles using the common provider_profile_search endpoint.
   * Returns the full parsed response including paged results and profile details.
   */
  async searchProfiles(
    request: PatinfoSearchRequestDto
  ): Promise<PatinfoSearchResponseDto> {
    let response: ServiceResponseDto;
    try {
      response = await this.callService('provider_profile_search', request);
    } catch (err: any) {
      throw new Error(
        `provider_profile_search failed: ${err?.message ?? 'unknown error'}`
      );
    }

    if (!response.success) {
      throw new Error(
        `provider_profile_search error: ${response.message || response.errorDescription}`
      );
    }

    try {
      return JSON.parse(response.payload) as PatinfoSearchResponseDto;
    } catch {
      throw new Error('provider_profile_search: failed to parse response payload');
    }
  }

  // ── New (create) ──────────────────────────────────────────────────────────

  /**
   * Create a provider profile via provider_profile_new.
   * Backend returns result=new|exists plus the profile payload.
   */
  async newProfile(
    request: PatinfoNewRequestDto
  ): Promise<PatinfoNewResponseDto> {
    let response: ServiceResponseDto;
    try {
      response = await this.callService('provider_profile_new', request);
    } catch (err: any) {
      throw new Error(
        `provider_profile_new failed: ${err?.message ?? 'unknown error'}`
      );
    }

    if (!response.success) {
      throw new Error(
        `provider_profile_new error: ${response.message || response.errorDescription}`
      );
    }

    try {
      return JSON.parse(response.payload) as PatinfoNewResponseDto;
    } catch {
      throw new Error('provider_profile_new: failed to parse response payload');
    }
  }

  // ── Put (create / update) ─────────────────────────────────────────────────

  /**
   * Create or update a provider profile.
   * Only include changed fields in the payload; omit unchanged sub-objects.
   */
  async putProfile(
    profile: PatinfoPutRequestDto
  ): Promise<PatinfoPutResponseDto> {
    let response: ServiceResponseDto;
    try {
      response = await this.callService('provider_profile_put', profile);
    } catch (err: any) {
      throw new Error(
        `provider_profile_put failed: ${err?.message ?? 'unknown error'}`
      );
    }

    if (!response.success) {
      throw new Error(
        `provider_profile_put error: ${response.message || response.errorDescription}`
      );
    }

    try {
      return JSON.parse(response.payload) as PatinfoPutResponseDto;
    } catch {
      throw new Error('provider_profile_put: failed to parse response payload');
    }
  }

  // ── Launch (secure assessment send) ───────────────────────────────────────

  /**
   * Insert (Status >= '2') or update a secure assessment launch via launch_secure_put.
   * Returns the backend-assigned LaunchSecureDto (with SSKey populated on success),
   * to be appended to the owning profile's `launches` locally.
   */
  async launchSecure(
    request: LaunchSecureDto
  ): Promise<LaunchSecureDto> {
    let response: ServiceResponseDto;
    try {
      response = await this.callService('launch_secure_put', request);
    } catch (err: any) {
      throw new Error(
        `launch_secure_put failed: ${err?.message ?? 'unknown error'}`
      );
    }

    if (!response.success) {
      throw new Error(
        `launch_secure_put error: ${response.message || response.errorDescription}`
      );
    }

    try {
      return JSON.parse(response.payload) as LaunchSecureDto;
    } catch {
      throw new Error('launch_secure_put: failed to parse response payload');
    }
  }
}
